import { forEach } from 'libraries/lodash'
import debug from 'libraries/front-end-core/core/debug'
import { AutomatedDocumentResponse, ClarilisDocumentResponse, Document, DocumentResponse, DocumentsSearchResponse, DocumentsSectionsResponse, FlatDocumentSection } from 'services/DocumentService'
import { parseJson, deepPush, touch } from 'helpers/transforms'

export function toSections (json: JSON): { nodes: Record<string, FlatDocumentSection>, root: string } {
  debug.time('toSections (documents)')
  const data: DocumentsSectionsResponse[] = parseJson(json)

  const nodes: Record<string, FlatDocumentSection> = {}
  let root = ''

  const ntouch = touch.bind(null, nodes)
  const npush = deepPush.bind(null, nodes)

  forEach(data, (section, index) => {
    const { id, title, parentId } = section
    if (index === 0) {
      root = id
      ntouch(id, { id, title, overview: null, path: [], childSections: [], descendants: [], parentId })
    } else if (id !== '' && parentId in nodes) {
      const parentPath = nodes[parentId].path
      const path = [...parentPath, id]

      ntouch(id, {
        id,
        title,
        overview: null,
        active: !!section.active,
        path,
        childSections: [],
        descendants: [id],
        areaOfLaw: 'corporate',
        parentId
      })
      npush([parentId, 'childSections'], id)
      parentPath.forEach(p => npush([p, 'descendants'], id))
    }
  })

  debug.timeEnd?.('toSections (documents)')

  return { nodes, root }
}

export function toDocuments (json: JSON): Document[] {
  debug.time?.('toDocuments')
  const data: DocumentResponse[] = parseJson(json)
  const transformed: Document[] = data.map(document => ({
    id: document.data.id,
    title: document.data.title,
    description: document.data.description,
    parentId: document.data.parentId,
    automated: false,
    clarilis: false,
    hasAccess: document.hasAccess,
    underMaintenance: document.data.underMaintenance,
    previewAvailable: document.data.previewAvailable,
    links: document.data.links,
    position: 0
  }))
  debug.timeEnd?.('toDocuments')
  return transformed
}

// Hard code position to 0 for automated and clarilis documents for now

export function toAutomatedDocuments (json: JSON): Document[] {
  debug.time?.('toAutomatedDocuments')
  const data: AutomatedDocumentResponse[] = parseJson(json)
  const transformed: Document[] = data.map(document => ({
    id: document.data.uuid,
    title: document.data.title,
    description: document.data.overview,
    parentId: document.data.parentSectionId,
    automated: true,
    clarilis: false,
    hasAccess: document.hasAccess,
    underMaintenance: document.data.underMaintenance,
    position: 0
  }))
  debug.timeEnd?.('toAutomatedDocuments')
  return transformed
}

export function toClarilisDocuments (json: JSON): Document[] {
  debug.time?.('toClarilisDocuments')
  const data: ClarilisDocumentResponse[] = parseJson(json)
  const transformed: Document[] = data.map(document => ({
    id: document.data.uuid,
    title: document.data.title,
    description: document.data.description,
    parentId: document.data.parentSectionId,
    automated: true,
    clarilis: true,
    hasAccess: document.hasAccess ?? false,
    underMaintenance: document.data.underMaintenance ?? false,
    cta: document.data.ctaLink,
    position: 0
  }))
  debug.timeEnd?.('toClarilisDocuments')
  return transformed
}

interface DocumentsSearchTransformed {
  resultCount: number
  results: Array<{
    id: string
    name: string | null
    title: string | null
    description: string
    parentId: string | null
    library: string | null
    position: string | null
    tiers: string[]
  }>
  flatFacets: Array<{ name: string, count: number }>
}

export function toDocumentsSearch (json: JSON): DocumentsSearchTransformed {
  const data: DocumentsSearchResponse = parseJson(json)

  return {
    resultCount: data.count,
    results: data.results.map(
      ({
        id,
        name,
        properties: { title, description, parentId, library, position },
        tiers
      }) => ({
        id,
        name: name !== '' ? name : null,
        title: title !== '' ? title : null,
        description,
        parentId: parentId !== '' ? parentId : null,
        library: library !== '' ? library : null,
        position: position !== '' ? position : null,
        tiers
      })
    ),
    flatFacets: data.facets
  }
}
